import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'

// Styles
import { shadows, fastTransition, fastDuration } from '../styles/effects'

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'bottom-start'
  | 'bottom-end'
type PreferredPlacement = 'auto' | Placement

const PLACEMENT_FALLBACKS: Record<Placement, Placement[]> = {
  right: ['right', 'left', 'bottom', 'top'],
  left: ['left', 'right', 'bottom', 'top'],
  bottom: ['bottom', 'top', 'right', 'left'],
  top: ['top', 'bottom', 'right', 'left'],
  'bottom-start': ['bottom-start'],
  'bottom-end': ['bottom-end'],
}

const AUTO_ORDER: Placement[] = ['right', 'left', 'bottom', 'top']

interface IProps {
  anchor: HTMLElement | null
  isOpen: boolean
  onClose: () => void
  placement?: PreferredPlacement
  offset?: number
  ariaLabel?: string
  /**
   * Where focus lands on open. 'first' suits a popover whose first control is the
   * point (a filter's search field); 'container' suits a list, where focusing row
   * one makes it look selected. Focus stays inside the dialog either way.
   */
  initialFocus?: 'first' | 'container'
  trapFocus?: boolean
  closeOnOutsideClick?: boolean
  closeOnEsc?: boolean
  children: React.ReactNode
}

interface IPosition {
  top: number
  left: number
  placement: Placement
}

const Popover: React.FC<IProps> = ({
  anchor,
  isOpen,
  onClose,
  placement = 'auto',
  offset = 12,
  ariaLabel,
  initialFocus = 'first',
  trapFocus = true,
  closeOnOutsideClick = true,
  closeOnEsc = true,
  children,
}) => {
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const [position, setPosition] = useState<IPosition | null>(null)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return
    }
    const compute = (): void => {
      const next = computePlacement(
        anchor,
        popoverRef.current,
        placement,
        offset
      )
      if (next) setPosition(next)
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [isOpen, placement, offset, anchor])

  // Remember what was focused when the popover opens and restore it only when it
  // actually closes or unmounts. Kept separate from the listener effect below,
  // whose deps include callbacks (e.g. onClose) that change identity on every
  // parent re-render: folding the focus-restore into that effect would yank focus
  // back to the trigger on every such render — e.g. while typing in a field that
  // live-updates app state on each keystroke.
  useEffect(() => {
    if (!isOpen) return
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null
    return () => {
      previouslyFocusedRef.current?.focus?.()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKey = (e: KeyboardEvent): void => {
      if (closeOnEsc && e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (trapFocus && e.key === 'Tab' && popoverRef.current) {
        const focusables = getFocusableElements(popoverRef.current)
        if (focusables.length === 0) {
          e.preventDefault()
          return
        }
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    const handlePointerDown = (e: MouseEvent | TouchEvent): void => {
      if (!closeOnOutsideClick) return
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (anchor?.contains(target)) return
      onClose()
    }

    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [isOpen, closeOnEsc, closeOnOutsideClick, trapFocus, onClose, anchor])

  useEffect(() => {
    if (!isOpen || !position) return
    if (initialFocus === 'container') {
      popoverRef.current?.focus()
      return
    }
    const first = popoverRef.current
      ? getFocusableElements(popoverRef.current)[0]
      : undefined
    first?.focus()
  }, [isOpen, position, initialFocus])

  if (!mounted || !isOpen) return null

  return ReactDOM.createPortal(
    <PopoverContainer
      ref={popoverRef}
      role="dialog"
      aria-modal="false"
      aria-label={ariaLabel}
      tabIndex={-1}
      style={
        position
          ? { top: position.top, left: position.left, opacity: 1 }
          : { opacity: 0, pointerEvents: 'none' }
      }
    >
      {children}
    </PopoverContainer>,
    document.body
  )
}

export default Popover

const computePlacement = (
  anchor: HTMLElement | null,
  popover: HTMLElement | null,
  preferred: PreferredPlacement,
  offset: number
): IPosition | null => {
  if (!anchor || !popover) return null
  const anchorRect = anchor.getBoundingClientRect()
  const popoverRect = popover.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  const candidates =
    preferred === 'auto' ? AUTO_ORDER : PLACEMENT_FALLBACKS[preferred]

  for (const p of candidates) {
    const pos = placementCoords(anchorRect, popoverRect, p, offset)
    if (fitsInViewport(pos, popoverRect, vw, vh)) {
      return { ...pos, placement: p }
    }
  }
  const fallback = placementCoords(
    anchorRect,
    popoverRect,
    candidates[0],
    offset
  )
  return { ...fallback, placement: candidates[0] }
}

const placementCoords = (
  anchor: DOMRect,
  popover: DOMRect,
  placement: Placement,
  offset: number
): { top: number; left: number } => {
  switch (placement) {
    case 'right':
      return {
        top: anchor.top + anchor.height / 2 - popover.height / 2,
        left: anchor.right + offset,
      }
    case 'left':
      return {
        top: anchor.top + anchor.height / 2 - popover.height / 2,
        left: anchor.left - popover.width - offset,
      }
    case 'bottom':
      return {
        top: anchor.bottom + offset,
        left: anchor.left + anchor.width / 2 - popover.width / 2,
      }
    case 'top':
      return {
        top: anchor.top - popover.height - offset,
        left: anchor.left + anchor.width / 2 - popover.width / 2,
      }
    case 'bottom-start':
      return {
        top: anchor.bottom + offset,
        left: anchor.left,
      }
    case 'bottom-end':
      return {
        top: anchor.bottom + offset,
        left: anchor.right - popover.width,
      }
  }
}

const fitsInViewport = (
  pos: { top: number; left: number },
  popover: DOMRect,
  vw: number,
  vh: number
): boolean =>
  pos.top >= 0 &&
  pos.left >= 0 &&
  pos.top + popover.height <= vh &&
  pos.left + popover.width <= vw

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

const getFocusableElements = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null
  )

const PopoverContainer = styled.div`
  position: fixed;
  z-index: 1200;
  background-color: ${({ theme }) => theme.colors.lightest};
  border-radius: 12px;
  box-shadow: ${shadows.depthFour};
  transition: opacity ${fastTransition};
  opacity: 0;
  outline: none;
  overflow: hidden;

  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${fastDuration / 4}ms ease-in-out;
  }
`
